import { useLang } from "@/i18n/LanguageContext";
import { motion } from "framer-motion";
import { ShieldCheck, FlaskConical, BookOpen } from "lucide-react";
import { ASSETS } from "@/lib/assets";

const ICONS = [ShieldCheck, FlaskConical, BookOpen];

export default function TrustSection() {
  const { t } = useLang();
  return (
    <section
      id="trust"
      data-testid="trust-section"
      className="relative scroll-mt-28 overflow-hidden bg-zinc-950 py-24 text-white md:py-32"
    >
      <img
        src={ASSETS.lifestyle}
        alt=""
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover opacity-20"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-zinc-950/30 via-zinc-950/85 to-zinc-950"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(800px 400px at 20% 20%, rgba(81,75,186,0.5), transparent), radial-gradient(700px 360px at 80% 60%, rgba(196,192,245,0.18), transparent)",
        }}
      />
      <div className="container-x relative">
        <div className="max-w-2xl">
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[color:var(--sentinel-accent)]" data-testid="trust-overline">
            {t.trust.overline}
          </span>
          <h2 className="mt-4 text-4xl text-white md:text-[56px] md:leading-[1.04]">
            {t.trust.title}
          </h2>
          <p className="mt-5 text-lg text-zinc-300">{t.trust.sub}</p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {t.trust.stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="rounded-2xl border border-white/10 bg-white/[0.06] p-6 text-center backdrop-blur"
              data-testid={`trust-stat-${i}`}
            >
              <div className="text-4xl font-semibold text-white md:text-5xl">{stat.value}</div>
              <p className="mt-2 text-sm text-zinc-400">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-3">
          {t.trust.pillars.map((p, i) => {
            const Icon = ICONS[i] || ShieldCheck;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-3xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur transition hover:border-[color:var(--sentinel-accent)]/60"
                data-testid={`trust-pillar-${i}`}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[color:var(--sentinel)] text-white">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-6 text-xl text-white">{p.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{p.d}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
